// Exports the admin dashboard's electricity outage schedule as a PNG.
//
// The image is drawn on a canvas from the table's data attributes instead of
// screenshotting the DOM: the table lives in a horizontally scrolling wrapper
// with a sticky first column, so a DOM screenshot would be clipped on narrow
// screens. Drawing it guarantees all 48 half-hour slots end up in the image,
// with the same layout on desktop and phone.

const SCALE = 2;
const PADDING = 24;
const FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif';

const QUEUE_COL_WIDTH = 72;
const SLOT_WIDTH = 30;
const SLOT_COUNT = 48;
const STAT_COL_WIDTH = 116;
const GROUP_HEADER_HEIGHT = 30;
const TIME_HEADER_HEIGHT = 46;
const ROW_HEIGHT = 30;

const BORDER = "#dee2e6";
const HEADER_BG = "#f8f9fa";
const TEXT = "#212529";
const MUTED = "#6c757d";

const DURATION_HEADER = ["Прогнозована", "тривалість", "відключень за", "добу, год"];
const PERCENTAGE_HEADER = ["Частка", "відключених", "годин у черзі", "з початку місяця"];

function readSchedule(table) {
  const rows = Array.from(table.querySelectorAll("tr.queue-row")).map((row) => ({
    queue: row.dataset.queue,
    color: row.dataset.color,
    slots: (row.dataset.slots || "").split("").map((s) => s === "1"),
    hours: row.dataset.hours,
    percentage: row.dataset.percentage,
  }));

  return {
    title: table.dataset.title,
    published: table.dataset.published,
    filename: table.dataset.filename || "grafik-vidkliuchen.png",
    rows,
  };
}

function slotLabel(index) {
  const minutes = index * 30;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;
}

function strokeRect(ctx, x, y, w, h) {
  ctx.strokeRect(x + 0.5, y + 0.5, w, h);
}

function fillText(ctx, text, x, y, { size = 12, weight = "normal", color = TEXT, align = "center" } = {}) {
  ctx.font = `${weight} ${size}px ${FONT}`;
  ctx.fillStyle = color;
  ctx.textAlign = align;
  ctx.textBaseline = "middle";
  ctx.fillText(text, x, y);
}

function fillMultiline(ctx, lines, x, centerY, opts) {
  const lineHeight = (opts.size || 12) + 3;
  const top = centerY - ((lines.length - 1) * lineHeight) / 2;
  lines.forEach((line, i) => fillText(ctx, line, x, top + i * lineHeight, opts));
}

function drawSchedule(schedule) {
  const tableWidth = QUEUE_COL_WIDTH + SLOT_COUNT * SLOT_WIDTH + STAT_COL_WIDTH * 2;
  const headerHeight = GROUP_HEADER_HEIGHT + TIME_HEADER_HEIGHT;
  const tableHeight = headerHeight + schedule.rows.length * ROW_HEIGHT;

  const legendColumns = 6;
  const legendRowHeight = 28;
  const legendRows = Math.ceil(schedule.rows.length / legendColumns);

  const titleHeight = 56;
  const legendTop = PADDING + titleHeight + tableHeight + 20;
  const width = PADDING * 2 + tableWidth;
  const height = legendTop + legendRows * legendRowHeight + 36 + PADDING;

  const canvas = document.createElement("canvas");
  canvas.width = width * SCALE;
  canvas.height = height * SCALE;
  const ctx = canvas.getContext("2d");
  ctx.scale(SCALE, SCALE);

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);

  fillText(ctx, schedule.title, width / 2, PADDING + titleHeight / 2 - 6, { size: 26, weight: "600" });

  const x0 = PADDING;
  const y0 = PADDING + titleHeight;
  const slotsX = x0 + QUEUE_COL_WIDTH;
  const durationX = slotsX + SLOT_COUNT * SLOT_WIDTH;
  const percentageX = durationX + STAT_COL_WIDTH;

  // Header background
  ctx.fillStyle = HEADER_BG;
  ctx.fillRect(x0, y0, tableWidth, headerHeight);

  ctx.strokeStyle = BORDER;
  ctx.lineWidth = 1;

  // Header cells spanning both header rows
  strokeRect(ctx, x0, y0, QUEUE_COL_WIDTH, headerHeight);
  fillMultiline(ctx, ["Черга/", "підчерга"], x0 + QUEUE_COL_WIDTH / 2, y0 + headerHeight / 2, { size: 12, weight: "bold" });
  strokeRect(ctx, durationX, y0, STAT_COL_WIDTH, headerHeight);
  fillMultiline(ctx, DURATION_HEADER, durationX + STAT_COL_WIDTH / 2, y0 + headerHeight / 2, { size: 11, weight: "bold" });
  strokeRect(ctx, percentageX, y0, STAT_COL_WIDTH, headerHeight);
  fillMultiline(ctx, PERCENTAGE_HEADER, percentageX + STAT_COL_WIDTH / 2, y0 + headerHeight / 2, { size: 11, weight: "bold" });

  // Group header + per-slot time header
  strokeRect(ctx, slotsX, y0, SLOT_COUNT * SLOT_WIDTH, GROUP_HEADER_HEIGHT);
  fillText(ctx, "30-хвилинні інтервали доби", slotsX + (SLOT_COUNT * SLOT_WIDTH) / 2, y0 + GROUP_HEADER_HEIGHT / 2, { size: 12, weight: "bold" });

  const timeY = y0 + GROUP_HEADER_HEIGHT;
  for (let i = 0; i < SLOT_COUNT; i++) {
    const x = slotsX + i * SLOT_WIDTH;
    if (i % 2 === 0) {
      ctx.fillStyle = "#eef0f2";
      ctx.fillRect(x, timeY, SLOT_WIDTH, TIME_HEADER_HEIGHT);
    }
    strokeRect(ctx, x, timeY, SLOT_WIDTH, TIME_HEADER_HEIGHT);
    const end = i + 1 === SLOT_COUNT ? "24:00" : slotLabel(i + 1);
    fillMultiline(ctx, [slotLabel(i), "─", end], x + SLOT_WIDTH / 2, timeY + TIME_HEADER_HEIGHT / 2, { size: 8, weight: "600" });
  }

  // Rows
  schedule.rows.forEach((row, r) => {
    const y = y0 + headerHeight + r * ROW_HEIGHT;

    ctx.fillStyle = row.color;
    ctx.fillRect(x0, y, QUEUE_COL_WIDTH, ROW_HEIGHT);
    strokeRect(ctx, x0, y, QUEUE_COL_WIDTH, ROW_HEIGHT);
    fillText(ctx, row.queue, x0 + QUEUE_COL_WIDTH / 2, y + ROW_HEIGHT / 2, { size: 13, weight: "bold", color: "#ffffff" });

    row.slots.forEach((off, i) => {
      const x = slotsX + i * SLOT_WIDTH;
      if (off) {
        ctx.fillStyle = row.color;
        ctx.fillRect(x, y, SLOT_WIDTH, ROW_HEIGHT);
      }
      ctx.strokeStyle = off ? "rgba(255, 255, 255, 0.6)" : BORDER;
      strokeRect(ctx, x, y, SLOT_WIDTH, ROW_HEIGHT);
    });

    ctx.strokeStyle = BORDER;
    strokeRect(ctx, durationX, y, STAT_COL_WIDTH, ROW_HEIGHT);
    fillText(ctx, row.hours, durationX + STAT_COL_WIDTH / 2, y + ROW_HEIGHT / 2, { size: 13, weight: "bold" });
    strokeRect(ctx, percentageX, y, STAT_COL_WIDTH, ROW_HEIGHT);
    fillText(ctx, row.percentage, percentageX + STAT_COL_WIDTH / 2, y + ROW_HEIGHT / 2, { size: 13, weight: "bold" });
  });

  // Outer border on top of the cell grid
  ctx.strokeStyle = "#ced4da";
  strokeRect(ctx, x0, y0, tableWidth, tableHeight);

  // Legend
  const legendColumnWidth = tableWidth / legendColumns;
  schedule.rows.forEach((row, i) => {
    const x = x0 + (i % legendColumns) * legendColumnWidth;
    const y = legendTop + Math.floor(i / legendColumns) * legendRowHeight;
    ctx.fillStyle = row.color;
    ctx.fillRect(x, y, 40, 22);
    fillText(ctx, row.queue, x + 20, y + 11, { size: 11, weight: "bold", color: "#ffffff" });
    fillText(ctx, `Черга ${row.queue}`, x + 50, y + 11, { size: 13, align: "left" });
  });

  if (schedule.published) {
    fillText(ctx, schedule.published, x0, legendTop + legendRows * legendRowHeight + 18, { size: 12, color: MUTED, align: "left" });
  }

  return canvas;
}

function downloadCanvas(canvas, filename) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Не вдалося створити зображення"));
        return;
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      resolve();
    }, "image/png");
  });
}

function initializeScheduleImageDownload() {
  const button = document.querySelector("[data-schedule-image-download]");
  const table = document.querySelector("[data-schedule-image-source]");
  if (!button || !table) return;

  button.addEventListener("click", async () => {
    button.disabled = true;
    try {
      const schedule = readSchedule(table);
      await downloadCanvas(drawSchedule(schedule), schedule.filename);
    } catch (error) {
      console.error("Schedule image export failed:", error);
      alert("Не вдалося зберегти зображення графіка");
    } finally {
      button.disabled = false;
    }
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializeScheduleImageDownload);
} else {
  initializeScheduleImageDownload();
}

export { drawSchedule, readSchedule };
